import React, { useState, useContext } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import Map from 'react-map-gl';
import { AbsoluteCenter, useDisclosure, Box } from '@chakra-ui/react';
import '../../App.css'
import { getUniqueVariables } from '../../utils/helpers';
import StationMarker from './StationMarker';
import ForecastModal from './ForecastModal';
import { getZoneForecast } from '../../utils/forecasts';
import MapTabs from './MapTabs';
import ParameterSelect from '../../components/ParameterSelect';
import StationPreview from './StationPreview';
import Forecasts from './Forecasts';
import { MAPBOX_TOKEN } from '../../utils/constants';
import useDatasetsMetadata from '../../components/customHooks/useDatasetsMetadata';
import useObservationData from '../../components/customHooks/useObservationData';
import useForecasts from '../../components/customHooks/useForecasts';
import Loading from '../../components/Loading';
import { PreferredUnitsContext } from '../../components/contexts/PreferredUnitsContext';
import Regions from './Regions';
import useRegions from '../../components/customHooks/useRegions';

const initialViewState = window.screen.width < 768
? {
  longitude: -150.7,
  latitude: 60.6,
  zoom: 3.2,
} : {
  longitude: -149.2,
  latitude: 59.7,
  zoom: 4.1,
};

const MapboxMap = () => {
  const [preferredUnitSystem] = useContext(PreferredUnitsContext);
  const [currentTab, setCurrentTab] = useState('stations');
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedParameter, setSelectedParameter] = useState('');
  const [selectedForecastZone, setSelectedForecastZone] = useState(null);
  const datasetsMetadata = useDatasetsMetadata();
  const observationData = useObservationData();
  const regionsGeoJson = useRegions();
  const forecasts = useForecasts();
  const [mapLocation, setMapLocation] = useState({});
  const [totalInteractiveLayers, setTotalInteractiveLayers] = useState();

  const parameters = getUniqueVariables(datasetsMetadata.data);

  const tidalBuoys = observationData.data?.filter(dataset => {
    const metadata = datasetsMetadata.data?.find(d => d?.datasetID === dataset?.datasetID);
    return metadata?.isTidalBuoy;
  });

  const stations = observationData.data?.filter(dataset => {
    const metadata = datasetsMetadata.data?.find(d => d?.datasetID === dataset?.datasetID);
    return !metadata?.isTidalBuoy;
  });

  const {
    isOpen: forecastModalIsOpen,
    onOpen: onOpenForecastModal,
    onClose: onCloseForecastModal
  } = useDisclosure();

  const onTabChange = (tab) => {
    setSelectedStation(null);
    setSelectedParameter('');
    setCurrentTab(tab);
  }

  const getTotalInteractiveLayers = (value) => {
    setTotalInteractiveLayers(value);
  }

  const onMapClick = (event) => {
    if (event.features && event.features[0]) {
      const zoneData = event.features[0].properties;

      const allForecasts = forecasts?.map((query) => query?.data).flatMap(data => data?.forecasts);

      if (forecasts?.length > 0) {
        const regionForecast = getZoneForecast(allForecasts, zoneData.ID);
        zoneData.forecast = regionForecast;
      }

      setSelectedForecastZone(zoneData);
      onOpenForecastModal();
    }
  };

  // Dynamically generates the interactive ID layers to match what is defined in <ForecastRegionLayer>
  const generateInteractiveLayerIds = (baseId) => {
    const ids = [];
    for (let i = 0; i < totalInteractiveLayers; i++) {
      ids.push(`${baseId}-${i}`);
    }
    return ids;
  };

  const interactiveLayerIds = [
    ...generateInteractiveLayerIds('region-fill-layer'),
    ...generateInteractiveLayerIds('warning-region-fill-layer'),
    ...generateInteractiveLayerIds('advisory-region-fill-layer'),
    ...generateInteractiveLayerIds('watch-region-fill-layer')
  ];

  return (
    <div
      style={{
        position: 'absolute',
        top: 80, // height of the header
        right: 0,
        bottom: 0,
        left: 0,
      }}
    >
      <Map
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={initialViewState}
        onMoveEnd={(e) => setMapLocation({
          coordinates: {
            longitude: e.viewState.longitude,
            latitude: e.viewState.latitude
          },
          zoom: e.viewState.zoom,
        })}
        mapStyle='mapbox://styles/mapbox/streets-v12'
        interactiveLayerIds={interactiveLayerIds}
        onClick={onMapClick}
      >
        {observationData.isLoading && (
          <AbsoluteCenter>
            <Loading />
          </AbsoluteCenter>
        )}
        <Regions mapLocation={mapLocation} regionsGeoJson={regionsGeoJson} />
        {(currentTab === 'stations' || currentTab === 'parameters')
          && stations?.length > 0
          && stations.map((station) => {
            return (
              <StationMarker
                key={station?.datasetID}
                station={station}
                isSelected={selectedStation?.datasetID === station?.datasetID}
                onClick={setSelectedStation}
                selectedParameter={selectedParameter}
                datasetsMetadata={datasetsMetadata.data}
                showValue={currentTab === 'parameters'}
                preferredUnitSystem={preferredUnitSystem}
                currentTab={currentTab}
              />
            )
          })}
        {currentTab === 'forecasts' &&
          <Forecasts
            tidalBuoys={tidalBuoys}
            onStationClick={setSelectedStation}
            setSelectedParameter={setSelectedParameter}
            selectedStation={selectedStation}
            selectedParameter={selectedParameter}
            forecasts={forecasts}
            datasetsMetadata={datasetsMetadata.data}
            preferredUnitSystem={preferredUnitSystem}
            getTotalInteractiveLayers={getTotalInteractiveLayers}
            regionsGeoJson={regionsGeoJson}
          />
        }
      </Map>
      {/* wanted to put ForecastModal in Forecasts component, but onMapClick 
        needed to call onOpenForecastModal, so it lives here for now */}
      <ForecastModal
        selectedForecastZone={selectedForecastZone}
        isOpen={forecastModalIsOpen}
        onOpen={onOpenForecastModal}
        onClose={onCloseForecastModal}
      />
      <div style={{ position: 'fixed', bottom: 80, zIndex: 1400, width: '100%' }}>
        {currentTab === 'parameters' && !selectedStation && parameters.length > 0 && (
          <Box px={4} className='parameter-select'>
            <ParameterSelect parameters={parameters} selectedParameter={selectedParameter} setSelectedParameter={setSelectedParameter} />
          </Box>
        )}
        {selectedStation && (
          <StationPreview
            selectedStation={selectedStation}
            setSelectedStation={setSelectedStation}
            selectedParameter={selectedParameter}
            stationMetadata={datasetsMetadata.data.find((d) => d.datasetID === selectedStation.datasetID)?.metadata}
            displayParameter={selectedParameter && (currentTab === 'parameters' || currentTab === 'forecasts')}
            preferredUnitSystem={preferredUnitSystem}
          />
        )}
      </div>
      <MapTabs currentTab={currentTab} onTabChange={onTabChange} />
    </div>
  );
};

export default MapboxMap;
